// src/features/records/components/CalendarView/utils/calendarData.js
import { 
  getCalendarDays, 
  isRecordOnDate, 
  sortRecordsByDate,
  getRecordDisplayDate,
  formatRecordTime
} from './dateHelpers';
import { isSameDay, startOfDay } from 'date-fns';

const translate = (t, key, fallback) => {
  if (typeof t === 'function') {
    return t(key, { defaultValue: fallback });
  }
  return fallback;
};

/**
 * Group records by date for calendar display
 * @param {Array} records - Array of records with date attributes
 * @param {Date} currentMonth - The current month being displayed
 * @returns {Map<string, Array>} Map of date strings to records
 */
export const groupRecordsByDate = (records, currentMonth) => {
  const recordsByDate = new Map();
  const calendarDays = getCalendarDays(currentMonth);
  
  // Initialize all calendar days
  calendarDays.forEach(day => {
    const dateKey = startOfDay(day).toISOString();
    recordsByDate.set(dateKey, []);
  });
  
  // Group records by their dates
  records.forEach(record => {
    // Skip records without dates
    if (!record?.attributes?.date?.start) return;
    
    // Check each calendar day to see if this record occurs on it
    calendarDays.forEach(day => {
      if (isRecordOnDate(record, day)) {
        const dateKey = startOfDay(day).toISOString();
        const dayRecords = recordsByDate.get(dateKey) || [];
        dayRecords.push(record);
        recordsByDate.set(dateKey, dayRecords);
      }
    });
  });
  
  // Sort records within each day by time
  recordsByDate.forEach((dayRecords, dateKey) => {
    recordsByDate.set(dateKey, sortRecordsByDate(dayRecords));
  });
  
  return recordsByDate;
};

/**
 * Get calendar grid data structure
 * @param {Array} records - Array of records
 * @param {Date} currentMonth - Current month being displayed
 * @returns {Object} Calendar grid data
 */
export const getCalendarGridData = (records, currentMonth) => {
  const calendarDays = getCalendarDays(currentMonth);
  const recordsByDate = groupRecordsByDate(records, currentMonth);
  
  const weeks = [];
  let currentWeek = [];
  
  calendarDays.forEach((day, index) => {
    const dateKey = startOfDay(day).toISOString();
    const dayRecords = recordsByDate.get(dateKey) || [];
    
    currentWeek.push({
      date: day,
      dateKey,
      records: dayRecords,
      isToday: isSameDay(day, new Date()),
      isCurrentMonth: day.getMonth() === currentMonth.getMonth()
    });
    
    // Start new week after Saturday (index 6, 13, 20, etc.)
    if ((index + 1) % 7 === 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  
  return {
    weeks,
    recordsByDate,
    totalRecords: records.length
  };
};

/**
 * Prepare record data for calendar display
 * @param {Object} record - Raw record data
 * @param {Object} formatOptions - Time formatting options (locale/t/timeZone)
 * @returns {Object} Prepared record for display
 */
export const prepareRecordForDisplay = (record, formatOptions = {}) => {
  const accentColor =
    record.calendarColor ||
    record.categoryColor ||
    record.color ||
    'var(--accent-primary)';

  const tintColor =
    record.calendarColorTint ||
    record.colorSoft ||
    'rgba(255, 87, 87, 0.12)';

  const tintHoverColor =
    record.calendarColorTintHover ||
    record.colorSoftHover ||
    'rgba(255, 87, 87, 0.2)';

  return {
    id: record.id,
    title:
      record.content?.title ||
      translate(formatOptions.t, 'records.views.calendar.item.untitled', 'Untitled'),
    time: formatRecordTime(record, formatOptions),
    isAllDay: record.attributes?.date?.isAllDay || false,
    startDate: getRecordDisplayDate(record),
    color: accentColor,
    tint: tintColor,
    tintHover: tintHoverColor,
    ownerId: record.access?.owner ?? record.ownerId ?? null
  };
};

/**
 * Get visible records for a date cell (max 3 + overflow count)
 * @param {Array} records - All records for the date
 * @param {Object} formatOptions - Time formatting options (locale/t/timeZone)
 * @returns {Object} Visible records and overflow count
 */
export const getVisibleRecords = (records, maxVisible = 3, formatOptions = {}) => {
  if (!records || records.length === 0) {
    return {
      visible: [],
      overflowCount: 0
    };
  }
  
  const sortedRecords = sortRecordsByDate(records);
  const visible = sortedRecords
    .slice(0, maxVisible)
    .map((record) => prepareRecordForDisplay(record, formatOptions));
  const overflowCount = Math.max(0, records.length - maxVisible);
  
  return {
    visible,
    overflowCount
  };
};

/**
 * Filter records for search within calendar view
 * @param {Array} records - Array of records
 * @param {string} searchQuery - Search query
 * @returns {Array} Filtered records
 */
export const filterRecordsForSearch = (records, searchQuery) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return records;
  }
  
  const query = searchQuery.toLowerCase().trim();
  
  return records.filter(record => {
    const title = record.content?.title?.toLowerCase() || '';
    const body = record.content?.body?.toLowerCase() || '';
    const location = record.attributes?.location?.name?.toLowerCase() || '';
    
    return title.includes(query) || 
           body.includes(query) || 
           location.includes(query);
  });
};

/**
 * Get records for a specific date range (for list view)
 * @param {Array} records - All records
 * @param {Date} startDate - Start of range
 * @param {Date} endDate - End of range
 * @returns {Array} Records within the date range
 */
export const getRecordsInRange = (records, startDate, endDate) => {
  return records.filter(record => {
    const recordDate = getRecordDisplayDate(record);
    if (!recordDate) return false;
    
    return recordDate >= startDate && recordDate <= endDate;
  });
};

/**
 * Calculate calendar statistics
 * @param {Map} recordsByDate - Records grouped by date
 * @param {Date} currentMonth - Current month
 * @returns {Object} Calendar statistics
 */
export const getCalendarStats = (recordsByDate, currentMonth) => {
  let totalEvents = 0;
  let daysWithEvents = 0;
  let busiestDay = null;
  let maxEventsInDay = 0;
  
  recordsByDate.forEach((records, dateKey) => {
    if (records.length > 0) {
      const date = new Date(dateKey);
      if (date.getMonth() === currentMonth.getMonth()) {
        totalEvents += records.length;
        daysWithEvents++;
        
        if (records.length > maxEventsInDay) {
          maxEventsInDay = records.length;
          busiestDay = date;
        }
      }
    }
  });
  
  return {
    totalEvents,
    daysWithEvents,
    busiestDay,
    maxEventsInDay,
    averageEventsPerDay: daysWithEvents > 0 ? (totalEvents / daysWithEvents).toFixed(1) : 0
  };
};
